/* eslint-disable react-hooks/exhaustive-deps */
import { throttle } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to force a component update.
 *
 * The hook automatically triggers a force update whenever the provided dependencies change.
 * Additionally, it returns a function that, when invoked, will force the component to re-render.
 * This can be useful in rare scenarios where React might not detect changes, and you need to
 * manually trigger a re-render.
 *
 * @param {Array} dependencies - List of dependencies to watch. Triggers a force update if any of these change.
 * @example
 * const forceUpdate = useForceUpdate([someDependency]);
 * forceUpdate(); // Can also be invoked manually to force a re-render.
 *
 * @returns {{forceUpdate:Function,tick:number}} Function to invoke for forcing a re-render.
 */
const useForceUpdate = (dependencies = []) => {
  const [tick, setTick] = useState(0);

  const forceUpdate = useCallback(() => {
    setTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    forceUpdate();
  }, [...dependencies]);

  return { forceUpdate, tick };
};

export default useForceUpdate;
